import { useState } from 'react';
import type { ModalidadTarifaTrabajo, TarifaEquipo } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

type Columna = 'POR_HORA' | 'PRECIO_CLIENTE' | 'FIJA_MENSUAL';

const COLUMNAS: Array<{ modalidad: Columna; etiqueta: string; valor: (tf: TarifaEquipo) => number | null }> = [
  { modalidad: 'POR_HORA', etiqueta: 'ficha.porHora', valor: (tf) => tf.porHoraCents },
  { modalidad: 'PRECIO_CLIENTE', etiqueta: 'ficha.precioParaCliente', valor: (tf) => tf.precioClienteCents },
  { modalidad: 'FIJA_MENSUAL', etiqueta: 'ficha.fijaMensual', valor: (tf) => tf.fijaMensualCents },
];

/**
 * Tarifas del equipo en esta casa, editables en la propia tabla.
 *
 * Tres importes por persona: lo que cuesta su hora (EUR/hora), lo que se le
 * cobra al cliente por cada hora suya (precio para el cliente) y la fija
 * mensual. Los dos primeros son los que se proponen al cargar horas en esta
 * casa. Cambiar uno no reescribe lo ya cargado: cierra la tarifa anterior y
 * abre otra desde hoy, y cada carga guarda el precio con el que se hizo.
 */
export function TarifasEquipoTabla({
  idioma,
  propiedadId,
  tarifas,
  onCambio,
}: {
  idioma: Idioma;
  /** La casa, o null para las tarifas generales (las de las horas internas). */
  propiedadId: string | null;
  tarifas: TarifaEquipo[];
  onCambio: () => void;
}) {
  const [editando, setEditando] = useState<{ userId: string; modalidad: Columna } | null>(null);
  const [texto, setTexto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dinero = (c: number) =>
    new Intl.NumberFormat(idioma === 'es' ? 'es-ES' : idioma === 'de' ? 'de-DE' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(c / 100);

  const abrir = (tf: TarifaEquipo, modalidad: Columna, valor: number | null) => {
    setError(null);
    setEditando({ userId: tf.userId, modalidad });
    setTexto(valor === null ? '' : (valor / 100).toFixed(2).replace('.', ',').replace(/,00$/, ''));
  };

  const guardar = async () => {
    if (!editando) return;
    const limpio = texto.trim().replace(/\s|€/g, '').replace(',', '.');
    const n = Number(limpio);
    if (!limpio || !Number.isFinite(n) || n < 0 || n > 1_000_000) {
      setError(t(idioma, 'finreg.indicaImporte'));
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      await api.finFijarTarifa({
        userId: editando.userId,
        propertyId: propiedadId ?? null,
        modalidad: editando.modalidad as ModalidadTarifaTrabajo,
        importeCents: Math.round(n * 100),
      });
      setEditando(null);
      onCambio();
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'finreg.noSeFijo'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, propiedadId ? 'ficha.tarifasEquipo' : 'fin.tarifasGenerales')}</h2>
      {!propiedadId && <p className="suave">{t(idioma, 'fin.tarifasGeneralesPie')}</p>}
      <div className="tabla-scroll">
        <table className="tabla-tarifas">
          <thead>
            <tr>
              <th scope="col">{t(idioma, 'interno.persona')}</th>
              <th scope="col">{t(idioma, 'ficha.rol')}</th>
              {COLUMNAS.map((c) => (
                <th key={c.modalidad} scope="col" className="num">
                  {t(idioma, c.etiqueta)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tarifas.map((tf) => (
              <tr key={tf.userId}>
                <td>{tf.nombre}</td>
                <td>{t(idioma, `rol.${tf.rol}`)}</td>
                {COLUMNAS.map((c) => {
                  const valor = c.valor(tf);
                  const abierta =
                    editando?.userId === tf.userId && editando.modalidad === c.modalidad;
                  return (
                    <td key={c.modalidad} className="num numerico">
                      {abierta ? (
                        <span className="tarifa-editor">
                          <input
                            type="text"
                            inputMode="decimal"
                            autoFocus
                            aria-label={`${tf.nombre} · ${t(idioma, c.etiqueta)}`}
                            value={texto}
                            disabled={guardando}
                            onChange={(e) => setTexto(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void guardar();
                              if (e.key === 'Escape') setEditando(null);
                            }}
                          />
                          <button type="button" disabled={guardando} onClick={() => void guardar()}>
                            {t(idioma, 'app.guardar')}
                          </button>
                          <button
                            type="button"
                            className="enlace"
                            disabled={guardando}
                            onClick={() => setEditando(null)}
                          >
                            {t(idioma, 'app.cancelar')}
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="tarifa-celda"
                          title={t(idioma, 'ficha.editarTarifa')}
                          onClick={() => abrir(tf, c.modalidad, valor)}
                        >
                          {valor === null ? <span className="suave">—</span> : dinero(valor)}
                          <span className="tarifa-lapiz" aria-hidden="true">
                            ✎
                          </span>
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && (
        <p className="aviso-demo" role="alert">
          {error}
        </p>
      )}
      <p className="suave" style={{ fontSize: '0.8125rem' }}>
        {t(idioma, 'ficha.tarifaPrioridad')} {t(idioma, 'ficha.tarifaPrecioPie')}
      </p>
    </section>
  );
}
